import React from 'react';
import { theme } from '../config/theme';
import { apiFetch } from '../api/client';

/** ===== Медиа с Authorization (image / video / audio / application) ===== */
const mediaCache = new Map();
const mediaQueue = [];
const MEDIA_MAX_CONCURRENT = 6;
const MEDIA_CACHE_MAX_ENTRIES = 96;
const MEDIA_CACHE_MAX_BYTES = 160 * 1024 * 1024;
let mediaActive = 0;
let mediaCacheEpoch = 0;

function mediaCacheKey(path) {
  let owner = '';
  try {
    const stored = JSON.parse(window.localStorage.getItem('ai_diary_auth') || '{}');
    owner = String(stored?.user?.username || stored?.user?.email || '');
  } catch {}
  return `${owner}::${String(path || '')}`;
}

function pumpMediaQueue() {
  if (mediaActive >= MEDIA_MAX_CONCURRENT || !mediaQueue.length) return;
  const job = mediaQueue.shift();
  mediaActive += 1;
  Promise.resolve()
    .then(job.task)
    .then(job.resolve, job.reject)
    .finally(() => {
      mediaActive = Math.max(0, mediaActive - 1);
      pumpMediaQueue();
    });
  pumpMediaQueue();
}

function scheduleMedia(task, priority = 'normal') {
  return new Promise((resolve, reject) => {
    const job = { task, resolve, reject };
    if (priority === 'high') mediaQueue.unshift(job);
    else mediaQueue.push(job);
    pumpMediaQueue();
  });
}

function pruneMediaCache() {
  const completed = [...mediaCache.entries()]
    .filter(([, entry]) => entry?.url && !entry?.promise)
    .sort((left, right) => Number(left[1]?.lastUsed || 0) - Number(right[1]?.lastUsed || 0));
  let bytes = completed.reduce((sum, [, entry]) => sum + Number(entry?.size || 0), 0);
  while (completed.length > MEDIA_CACHE_MAX_ENTRIES || bytes > MEDIA_CACHE_MAX_BYTES) {
    const [key, entry] = completed.shift() || [];
    if (!key || !entry) break;
    mediaCache.delete(key);
    bytes -= Number(entry.size || 0);
    try { URL.revokeObjectURL(entry.url); } catch {}
  }
}

export function clearSecureMediaCache() {
  mediaCacheEpoch += 1;
  for (const entry of mediaCache.values()) {
    try { if (entry?.url) URL.revokeObjectURL(entry.url); } catch {}
  }
  mediaCache.clear();
}

export async function getSecureMediaAsset(path, options = {}) {
  const key = mediaCacheKey(path);
  if (!String(path || '').trim()) throw new Error('media path is required');
  const cached = mediaCache.get(key);
  if (cached?.url && cached?.blob) {
    cached.lastUsed = Date.now();
    return cached;
  }
  if (cached?.promise) return cached.promise;

  const { priority = 'normal', token, logout, ...requestOptions } = options || {};
  const requestEpoch = mediaCacheEpoch;
  const promise = scheduleMedia(async () => {
    const isShare = typeof path === 'string' && path.startsWith('/records/share/');
    const response = await apiFetch(path, {
      token,
      logout,
      ...(isShare ? { noAuth: true, credentials: 'omit' } : {}),
      ...requestOptions,
    });
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (requestEpoch !== mediaCacheEpoch) {
      URL.revokeObjectURL(objectUrl);
      throw new Error('media cache was cleared');
    }
    const asset = {
      blob,
      url: objectUrl,
      contentType: blob.type || response.headers.get('content-type') || '',
      size: Number(blob.size || 0),
      lastUsed: Date.now(),
      promise: null,
    };
    mediaCache.set(key, asset);
    pruneMediaCache();
    return asset;
  }, priority);
  mediaCache.set(key, { promise, lastUsed: Date.now(), url: '', blob: null, size: 0 });
  try {
    return await promise;
  } catch (error) {
    if (mediaCache.get(key)?.promise === promise) mediaCache.delete(key);
    throw error;
  }
}

export async function getSecureMediaObjectUrl(path, options = {}) {
  return (await getSecureMediaAsset(path, options)).url;
}

export function prefetchSecureMedia(path, options = {}) {
  if (!String(path || '').trim()) return Promise.resolve(null);
  return getSecureMediaAsset(path, { ...options, priority: options?.priority || 'normal' }).catch(() => null);
}

if (typeof window !== 'undefined') {
  window.addEventListener('noesa:auth-cleared', clearSecureMediaCache);
}

export function SecureMedia({
  path,
  type,
  token,
  logout,
  style,
  alt,
  controls = true,
  autoPlay = false,
  loop = false,
  muted = false,
  playsInline = true,
  controlsList,
  lazy = false,
  rootMargin = '320px',
}) {
  const [url, setUrl] = React.useState(null);
  const [visible, setVisible] = React.useState(!lazy);
  const observerRef = React.useRef(null);
  const key = String(path || "");

  React.useEffect(() => {
    if (!lazy || visible) return undefined;
    const element = observerRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setVisible(true);
      observer.disconnect();
    }, { rootMargin });
    observer.observe(element);
    return () => observer.disconnect();
  }, [lazy, rootMargin, visible]);

  React.useEffect(() => {
    if (!visible) return undefined;
    let revoked = false;
    (async () => {
      try {
        if (!key) return;
        const asset = await getSecureMediaAsset(path, { token, logout });
        if (!revoked) setUrl(asset.url);
      } catch {
        // no-op
      }
    })();
    return () => {
      revoked = true;
    };
  }, [key, path, token, logout, visible]);

  if (!url) {
    if (!lazy) return null;
    return <span ref={observerRef} aria-hidden="true" style={style} />;
  }

  if (type === "image") {
    return <img ref={observerRef} src={url} alt={alt || ""} style={style} loading="lazy" decoding="async" />;
  }
  if (type === "video") {
    return (
      <video
        src={url}
        style={style}
        controls={controls}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        {...(controlsList ? { controlsList } : {})}
      />
    );
  }
  if (type === "audio") {
    return <audio src={url} controls style={style} />;
  }
  if (type === "application") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${theme.border}`, borderRadius: 8, background: "transparent", textDecoration: "none", ...(style || {}) }}>
        📎 Открыть файл
      </a>
    );
  }
  return null;
}

/** ===== Голосовой ввод (с MIME-подбором + fallback для iOS) ===== */
export function VoiceRecorder({ onResult, apiUrl, token, buttonLabel = "Голосом", style, logout }) {
  const [recording, setRecording] = React.useState(false);
  const [mediaRecorder, setMediaRecorder] = React.useState(null);
  const [blip, setBlip] = React.useState(0);
  const streamRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setBlip((b) => (b + 1) % 3), 350);
    return () => clearInterval(t);
  }, [recording]);

  function guessExt(mime) {
    if (mime.includes("webm")) return ".webm";
    if (mime.includes("mp4")) return ".mp4";
    if (mime.includes("ogg")) return ".ogg";
    if (mime.includes("wav")) return ".wav";
    return ".webm";
  }

  function isIOSLike() {
    try {
      const ua = String(navigator?.userAgent || "");
      const platform = String(navigator?.platform || "");
      return /iPad|iPhone|iPod/i.test(ua)
        || (platform === "MacIntel" && Number(navigator?.maxTouchPoints || 0) > 1);
    } catch {
      return false;
    }
  }

  async function startRecording() {
    try {
      if (isIOSLike() || !navigator?.mediaDevices || !window.MediaRecorder) {
        fileInputRef.current?.click();
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType });
        const file = new File([blob], `recording${guessExt(mimeType)}`, { type: mimeType });
        
        try {
          const formData = new FormData();
          formData.append("file", file);
          // Используем общий API-клиент, чтобы куки/credentials и CORS были настроены одинаково
          const res = await apiFetch('/voice/stt', { method: 'POST', body: formData });
          const data = await res.json();
          onResult?.(data.text || "");
        } catch (error) {
          console.error("STT error:", error);
          onResult?.("");
        }
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setRecording(true);
    } catch (error) {
      console.error("Recording error:", error);
      // Fallback to file input for iOS
      fileInputRef.current?.click();
    }
  }

  function stopRecording() {
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      setRecording(false);
      setMediaRecorder(null);
    }
  }

  async function handleFileUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiFetch('/voice/stt', { method: 'POST', body: formData });
      const data = await res.json();
      onResult?.(data.text || "");
    } catch (error) {
      console.error("STT error:", error);
      onResult?.("");
    }
  }

  return (
    <>
      <button
        onClick={recording ? stopRecording : startRecording}
        className="btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderRadius: 10,
          border: `1px solid ${theme.border}`,
          background: recording ? theme.danger : "transparent",
          color: recording ? "white" : theme.text,
          cursor: "pointer",
          ...(style || {}),
        }}
      >
        {recording ? (
          <>
            <span style={{ animation: "pulse 1s infinite" }}>🔴</span>
            {"Запись" + ".".repeat(blip)}
          </>
        ) : (
          <>
            <span>🎤</span>
            {buttonLabel}
          </>
        )}
      </button>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        capture="user"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
    </>
  );
}
